/**
 * RationaleList — the tutor-feedback F2 per-axis "why": one line each for the
 * accuracy and reasoning grades, explaining *why* the student got that grade.
 * Emitted by FreeformRubricV1 as `accuracyRationale` / `reasoningRationale`.
 * Part of the gated v2 body (see the `analysis_view_v2` gate); renders nothing
 * when both are empty. See docs/FeatureDesigns/diagnosis/tutor-feedback-model.md.
 *
 * A rationale earns its space only when it tells the student something the
 * score does not, so two cases are suppressed:
 *
 *  - **Full marks.** "Why did I get 10/10?" answers itself; the line reduces to
 *    a restatement ("the final answer is correct"). Applied per axis, so a
 *    10/10 accuracy with an imperfect approach still explains the approach.
 *  - **A wrong MCQ.** With a fixed set of options and nothing but a choice to
 *    inspect, a zero-accuracy rationale can only say the chosen option was
 *    wrong — which the score and the marked-correct option already say. This is
 *    specific to choice questions: on a numerical or freeform answer, a zero
 *    accuracy rationale is where the actual explanation lives, so it stays.
 */
import { LatexText } from '../../latex';

/** Max on every axis; scores are rendered out of 10 throughout the app. */
const FULL_MARKS = 10;

interface Props {
    accuracyRationale?: string;
    reasoningRationale?: string;
    /** Computed accuracy score (`computedScores.correctnessScore`), 0–10. */
    accuracyScore?: number;
    /** Computed approach score (`computedScores.approachScore`), 0–10. */
    reasoningScore?: number;
    /** The question's type — only choice questions suppress a zero-accuracy
     *  rationale. Absent (unknown) keeps the rationale: the suppression has to
     *  be earned, and hiding an explanation on a wrong answer is the worse
     *  failure. */
    questionType?: string | null;
}

export function RationaleList({
    accuracyRationale, reasoningRationale, accuracyScore, reasoningScore, questionType,
}: Props) {
    const isChoiceQuestion = questionType === 'MCQ' || questionType === 'MCQ_MULTI';
    const hideAccuracy =
        accuracyScore === FULL_MARKS
        || (isChoiceQuestion && accuracyScore === 0);
    const hideReasoning = reasoningScore === FULL_MARKS;

    const items = [
        !hideAccuracy && accuracyRationale?.trim()
            ? { label: 'Accuracy score reason', text: accuracyRationale } : null,
        !hideReasoning && reasoningRationale?.trim()
            ? { label: 'Approach score reason', text: reasoningRationale } : null,
    ].filter((x): x is { label: string; text: string } => x !== null);
    if (items.length === 0) return null;
    return (
        <section className="sol-section sol-rationale">
            {items.map(({ label, text }) => (
                <p key={label} className="sol-rationale-item">
                    <span className="sol-rationale-label">{label}:</span>
                    <LatexText text={text} />
                </p>
            ))}
        </section>
    );
}
